import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, CheckCircle2, MessageCircle, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { siteConfig, openingHours, whatsappLink } from "@/config/site";
import { IMAGES } from "@/config/images";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/ui-kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", surname: "", email: "", phone: "", vehicle_brand: "", vehicle_model: "",
  license_plate: "", service: "", appointment_date: "", message: "", privacy_accepted: false, company: "",
};

export default function Contact() {
  const { lang, t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = true;
    if (!form.phone.trim()) e.phone = true;
    if (!form.message.trim()) e.message = true;
    if (!form.privacy_accepted) e.privacy = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/appointments`, { ...form, language: lang });
      setSent(true);
      setForm(empty);
      toast.success(c.success);
    } catch (err) {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = (err) =>
    `w-full bg-[var(--bg-primary)] border ${err ? "border-[var(--accent)]" : "border-white/15"} px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors`;
  const label = "block text-xs uppercase tracking-wider text-zinc-500 mb-2";

  const F = c.fields;

  return (
    <div>
      <PageHero image={IMAGES.wheel} alt={t.alts.contactHero} overline={t.nav.contact} titleLines={[c.heroTitle]} subtitle={c.heroSub} />

      <section className="py-20 md:py-28 bg-[var(--bg-primary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Contact info */}
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-display text-2xl font-medium text-white">{c.infoTitle}</h2>
              <p className="mt-2 text-sm text-zinc-400">{siteConfig.companyName}</p>
              <ul className="mt-8 space-y-5 text-sm">
                <li className="flex items-start gap-3 text-zinc-300">
                  <MapPin size={18} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                  <span>{siteConfig.address.street}<br />{siteConfig.address.city}, {siteConfig.address.country}</span>
                </li>
                <li>
                  <a href={siteConfig.phoneHref} data-testid="contact-phone" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors">
                    <Phone size={18} style={{ color: "var(--accent)" }} /> {siteConfig.phone}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${siteConfig.email}`} data-testid="contact-email" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors">
                    <Mail size={18} style={{ color: "var(--accent)" }} /> {siteConfig.email}
                  </a>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <Clock size={18} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                  <span>
                    {openingHours.map((h) => (
                      <span key={h.dayKey} className="flex justify-between gap-8">
                        <span>{t.hours[h.dayKey]}</span>
                        <span className="text-zinc-500">{h.time === "closed" ? t.hours.closed : h.time}</span>
                      </span>
                    ))}
                  </span>
                </li>
              </ul>
              <a href={whatsappLink(t.cta.whatsappMessage)} target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp" className="on-dark mt-8 inline-flex items-center gap-2 text-sm font-semibold text-white px-6 py-3.5" style={{ background: "#25D366" }}>
                <MessageCircle size={17} fill="white" /> {t.cta.whatsapp}
              </a>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="bg-[var(--bg-secondary)] border border-white/10 p-6 md:p-10">
                {sent ? (
                  <div data-testid="contact-success" className="py-16 text-center">
                    <CheckCircle2 size={56} style={{ color: "var(--accent)" }} className="mx-auto" />
                    <p className="mt-6 font-display text-2xl text-white font-medium max-w-md mx-auto">{c.success}</p>
                    <button onClick={() => setSent(false)} data-testid="contact-reset" className="mt-8 text-sm text-zinc-400 underline hover:text-white">
                      {t.cta.appointment} →
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="appointment-form" noValidate>
                    <h2 className="font-display text-2xl font-medium text-white mb-8">{c.formTitle}</h2>

                    {/* honeypot */}
                    <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)}
                      tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className={label}>{F.name} *</label>
                        <input data-testid="field-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls(errors.name)} />
                      </div>
                      <div>
                        <label className={label}>{F.surname}</label>
                        <input data-testid="field-surname" value={form.surname} onChange={(e) => set("surname", e.target.value)} className={inputCls(false)} />
                      </div>
                      <div>
                        <label className={label}>{F.email} *</label>
                        <input data-testid="field-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} />
                      </div>
                      <div>
                        <label className={label}>{F.phone} *</label>
                        <input data-testid="field-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls(errors.phone)} />
                      </div>
                      <div>
                        <label className={label}>{F.brand}</label>
                        <input data-testid="field-brand" value={form.vehicle_brand} onChange={(e) => set("vehicle_brand", e.target.value)} className={inputCls(false)} />
                      </div>
                      <div>
                        <label className={label}>{F.model}</label>
                        <input data-testid="field-model" value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} className={inputCls(false)} />
                      </div>
                      <div>
                        <label className={label}>{F.plate}</label>
                        <input data-testid="field-plate" value={form.license_plate} onChange={(e) => set("license_plate", e.target.value)} className={inputCls(false)} />
                      </div>
                      <div>
                        <label className={label}>{F.service}</label>
                        <select data-testid="field-service" value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls(false)}>
                          <option value="">—</option>
                          {c.serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={label}>{F.date}</label>
                        <input data-testid="field-date" type="date" value={form.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} className={inputCls(false)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={label}>{F.message} *</label>
                        <textarea data-testid="field-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls(errors.message)} />
                      </div>
                    </div>

                    <label className="mt-6 flex items-start gap-3 cursor-pointer">
                      <input data-testid="field-privacy" type="checkbox" checked={form.privacy_accepted} onChange={(e) => set("privacy_accepted", e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[var(--accent)]" />
                      <span className={`text-sm ${errors.privacy ? "text-[var(--accent)]" : "text-zinc-400"}`}>{c.privacy} *</span>
                    </label>

                    <button type="submit" data-testid="submit-appointment" disabled={sending}
                      className="on-dark mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-semibold text-white px-8 py-4 disabled:opacity-60 transition-transform hover:-translate-y-0.5"
                      style={{ background: "var(--accent)" }}>
                      {sending ? c.sending : c.submit} {!sending && <ArrowRight size={16} />}
                    </button>
                    <p className="mt-3 text-xs text-zinc-600">* {c.required}</p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-[var(--bg-primary)] pb-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="relative border border-white/10 overflow-hidden">
            <iframe
              title="map"
              data-testid="map-embed"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(siteConfig.maps.embedQuery)}&z=15&output=embed`}
              className="w-full h-[360px] md:h-[440px] grayscale contrast-125"
              style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a href={siteConfig.maps.directionsUrl} target="_blank" rel="noopener noreferrer"
              data-testid="map-directions" className="absolute bottom-5 left-5 inline-flex items-center gap-2 text-sm font-semibold text-white px-6 py-3.5"
              style={{ background: "var(--accent)" }}>
              <MapPin size={16} /> {t.cta.route}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
